/**
 * Small persistence layer over the Node API (`/api/...`) with a localStorage fallback,
 * so the editor keeps working offline (PWA) or without a backend.
 * A 401 or 403 is not offline: it is thrown so a rejected save is not treated as success.
 */

import { apiJson, isAuthError } from '../auth/session.ts';

const LS_PREFIX = 'ple:';

const assetListeners = new Set<(kind: string, id: string, data: unknown) => void>();

function offline<T>(err: unknown, fallback: () => T): T {
  if (isAuthError(err)) throw err;
  return fallback();
}

/** Called after a local save (and a server PUT when the API is reachable). Lobby uses this to share tiles. */
export function onAssetSaved(cb: (kind: string, id: string, data: unknown) => void): () => void {
  assetListeners.add(cb);
  return () => assetListeners.delete(cb);
}

export function storeAssetLocally(kind: string, id: string, data: unknown): void {
  try {
    localStorage.setItem(`${LS_PREFIX}asset:${kind}:${id}`, JSON.stringify(data));
  } catch {
    /* quota or no storage */
  }
}

/** Project-wide JSON documents (e.g. kind 'terrain-style'), keyed by kind + id. The server answers 204 when absent. */
export async function loadAsset<T>(kind: string, id: string): Promise<T | null> {
  const local = (): T | null => {
    const v = localStorage.getItem(`${LS_PREFIX}asset:${kind}:${id}`);
    return v ? (JSON.parse(v) as T) : null;
  };
  try {
    return (await apiJson<T | undefined>('GET', `/api/assets/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`)) ?? local();
  } catch (err) {
    return offline(err, local);
  }
}

export async function saveAsset(kind: string, id: string, data: unknown): Promise<void> {
  storeAssetLocally(kind, id, data);
  try {
    await apiJson('PUT', `/api/assets/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, data);
  } catch (err) {
    if (isAuthError(err)) throw err;
    /* offline: kept locally */
  }
  for (const l of assetListeners) l(kind, id, data);
}

export async function listAssets(kind: string): Promise<string[]> {
  try {
    return await apiJson<string[]>('GET', `/api/assets/${encodeURIComponent(kind)}`);
  } catch (err) {
    if (isAuthError(err)) throw err;
    const out: string[] = [];
    const pre = `${LS_PREFIX}asset:${kind}:`;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (k.startsWith(pre)) out.push(k.slice(pre.length));
    }
    return out;
  }
}

export interface LevelSummary {
  id: string;
  name: string;
  updatedAt: string;
  /** newest version with a thumbnail (server only) */
  thumbVersion?: string;
  ownerId?: string | null;
  ownerEmail?: string | null;
}

export async function listLevels(): Promise<LevelSummary[]> {
  try {
    return await apiJson<LevelSummary[]>('GET', '/api/levels');
  } catch (err) {
    if (isAuthError(err)) throw err;
    const out: LevelSummary[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)!;
      if (!k.startsWith(`${LS_PREFIX}level:`)) continue;
      try {
        const d = JSON.parse(localStorage.getItem(k)!);
        out.push({ id: d.id, name: d.name, updatedAt: d.updatedAt ?? '' });
      } catch {
        /* skip */
      }
    }
    return out;
  }
}

function localLevel<T>(id: string): (T & { updatedAt?: string }) | null {
  try {
    const v = localStorage.getItem(`${LS_PREFIX}level:${id}`);
    return v ? JSON.parse(v) : null;
  } catch {
    return null;
  }
}

/** Server copy, unless the local copy is newer (saved offline, or the tab closed before the upload finished). */
export async function loadLevel<T>(id: string): Promise<T | null> {
  const local = localLevel<T>(id);
  let remote: (T & { updatedAt?: string }) | null = null;
  try {
    remote = await apiJson<T & { updatedAt?: string }>('GET', `/api/levels/${encodeURIComponent(id)}`);
  } catch (err) {
    return offline(err, () => local);
  }
  if (local && (!remote || (local.updatedAt ?? '') > (remote.updatedAt ?? ''))) {
    void saveLevel(local as unknown as { id: string });
    return local;
  }
  return remote ?? local;
}

/** Synchronous local copy (stamped with the save time); used when the page is being hidden or closed. */
export function saveLevelLocally(doc: { id: string }): boolean {
  try {
    localStorage.setItem(`${LS_PREFIX}level:${doc.id}`, JSON.stringify({ ...doc, updatedAt: new Date().toISOString() }));
    return true;
  } catch {
    return false;
  }
}

/** Saves locally first, then to the server. Returns true when the server accepted it. */
export async function saveLevel(doc: { id: string }): Promise<boolean> {
  saveLevelLocally(doc);
  try {
    const r = await apiJson<{ updatedAt?: string }>('PUT', `/api/levels/${encodeURIComponent(doc.id)}`, doc);
    if (r?.updatedAt) {
      try {
        localStorage.setItem(`${LS_PREFIX}level:${doc.id}`, JSON.stringify({ ...doc, updatedAt: r.updatedAt }));
      } catch {
        /* quota */
      }
    }
    return true;
  } catch (err) {
    if (isAuthError(err)) throw err;
    return false;
  }
}

export async function deleteLevel(id: string): Promise<void> {
  localStorage.removeItem(`${LS_PREFIX}level:${id}`);
  try {
    await apiJson('DELETE', `/api/levels/${encodeURIComponent(id)}`);
  } catch (err) {
    if (isAuthError(err)) throw err;
    /* offline */
  }
}
